import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Building2, Loader2, Sparkles, ShieldCheck, Zap, ArrowLeft, Check } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import { LanguageToggle } from "../components/LanguageToggle";

/**
 * Public tenant signup — "Criar minha empresa".
 *
 * Creates a brand-new tenant, its first admin user, and logs in.
 * After signup we redirect to `/` (the new admin lands on the empty dashboard
 * of their fresh tenant).
 */
export default function Signup() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get("plan") || "";

  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    if (!planId) return;
    let alive = true;
    api
      .get("/public/plans")
      .then(({ data }) => {
        if (!alive || !Array.isArray(data)) return;
        const p = data.find((x) => x.id === planId);
        if (p) setSelectedPlan(p);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [planId]);

  const [form, setForm] = useState({
    company_name: "",
    admin_name: "",
    email: "",
    phone: "",
    password: "",
    confirm: "",
  });
  const [submitting, setSubmitting] = useState(false);

  function setField(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e) {
    e.preventDefault();
    if (submitting) return;
    if (form.password.length < 6) {
      toast.error(t("A senha precisa ter ao menos 6 caracteres"));
      return;
    }
    if (form.password !== form.confirm) {
      toast.error(t("As senhas não coincidem"));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/signup", {
        company_name: form.company_name.trim(),
        admin_name: form.admin_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        password: form.password,
        plan: planId || undefined,
      });
      if (data?.access_token) {
        localStorage.setItem("cm_token", data.access_token);
      }
      if (setUser) setUser(data.user);
      localStorage.setItem("cm_start_tour", "1");
      toast.success(
        t('Empresa "{name}" criada! Você tem 14 dias de trial.', { name: data.tenant.name }),
      );
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      toast.error(msg || t("Falha ao criar empresa"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-white to-blue-50/40 flex items-center justify-center p-4"
      data-testid="signup-page"
    >
      <div className="w-full max-w-5xl grid md:grid-cols-2 gap-8 items-center">
        {/* Marketing side */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
          className="hidden md:block space-y-8 pr-4"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold">
            <Sparkles size={14} /> {t("14 dias grátis · sem cartão")}
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight">
            {t("A operação da sua empresa de limpeza,")}{" "}
            <span className="text-blue-600">{t("no piloto automático")}</span>.
          </h1>
          <p className="text-slate-600 text-lg">
            {t("Agende equipes, valide geolocalização, mande recibos no WhatsApp e controle financeiro — tudo em um só lugar.")}
          </p>
          <div className="space-y-3">
            <Feature icon={<ShieldCheck size={18} />} title={t("Seus dados, isolados")}>
              {t("Cada empresa só vê seus próprios clientes, colaboradores e finanças.")}
            </Feature>
            <Feature icon={<Zap size={18} />} title={t("Pronto em 30 segundos")}>
              {t("Crie sua conta, convide seu primeiro colaborador e comece a agendar.")}
            </Feature>
            <Feature icon={<Building2 size={18} />} title="White-label">
              {t("Personalize logo, cores e dados da empresa para recibos e PDFs.")}
            </Feature>
          </div>
        </motion.div>

        {/* Form side */}
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white border border-slate-200 rounded-2xl shadow-xl p-6 sm:p-8 space-y-4"
          data-testid="signup-form"
        >
          <div className="space-y-1">
            <div className="flex items-start justify-between gap-2">
              <h2 className="text-2xl font-bold text-slate-900">
                {t("Criar minha empresa")}
              </h2>
              <LanguageToggle compact testId="signup-language-toggle" />
            </div>
            <p className="text-sm text-slate-500">
              {t("Em menos de 1 minuto você está agendando a primeira limpeza.")}
            </p>
            <Link
              to="/"
              data-testid="signup-home-link"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-blue-600 transition pt-1"
            >
              <ArrowLeft size={15} /> {t("Voltar ao site")}
            </Link>
          </div>

          {selectedPlan && (
            <div
              data-testid="signup-selected-plan"
              className="rounded-xl border border-blue-200 bg-blue-50/70 p-4"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                  {t("Plano selecionado")}
                </div>
                <div className="text-lg font-bold text-slate-900">
                  {selectedPlan.name}
                  <span className="text-sm font-medium text-slate-500">
                    {" "}
                    · ${selectedPlan.price_usd}/mês
                  </span>
                </div>
              </div>
              <ul className="mt-2 space-y-1">
                {(selectedPlan.features || []).slice(0, 4).map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-slate-700">
                    <Check size={14} className="text-emerald-600 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-blue-700/80">
                {t("14 dias grátis. Você paga depois em Plano & Cobrança.")}
              </p>
            </div>
          )}

          <Field
            label={t("Nome da empresa")}
            placeholder={t("Ex.: Clean Manager USA")}
            value={form.company_name}
            onChange={(v) => setField("company_name", v)}
            required
            testid="signup-company-name"
          />
          <Field
            label={t("Seu nome")}
            placeholder={t("Ex.: Maria Silva")}
            value={form.admin_name}
            onChange={(v) => setField("admin_name", v)}
            required
            testid="signup-admin-name"
          />
          <Field
            label={t("E-mail (será seu login)")}
            type="email"
            placeholder="voce@empresa.com"
            value={form.email}
            onChange={(v) => setField("email", v)}
            required
            testid="signup-email"
          />
          <Field
            label={t("Telefone (opcional)")}
            placeholder="+1 (857) 555-1234"
            value={form.phone}
            onChange={(v) => setField("phone", v)}
            testid="signup-phone"
          />
          <div className="grid grid-cols-2 gap-3">
            <Field
              label={t("Senha")}
              type="password"
              placeholder={t("Mínimo 6 caracteres")}
              value={form.password}
              onChange={(v) => setField("password", v)}
              required
              testid="signup-password"
            />
            <Field
              label={t("Confirmar senha")}
              type="password"
              placeholder={t("Digite novamente")}
              value={form.confirm}
              onChange={(v) => setField("confirm", v)}
              required
              testid="signup-confirm"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="signup-submit"
            className="w-full mt-2 inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 text-white font-semibold py-3 hover:bg-blue-700 disabled:bg-blue-300 transition"
          >
            {submitting ? (
              <>
                <Loader2 size={18} className="animate-spin" /> {t("Criando empresa…")}
              </>
            ) : (
              <>{t("Criar minha empresa")}</>
            )}
          </button>

          <p className="text-center text-sm text-slate-500 pt-2">
            {t("Já tem conta?")}{" "}
            <Link
              to="/login"
              className="text-blue-600 font-semibold hover:underline"
              data-testid="signup-to-login"
            >
              {t("Entrar")}
            </Link>
          </p>
        </motion.form>
      </div>
    </div>
  );
}

function Feature({ icon, title, children }) {
  return (
    <div className="flex gap-3 items-start">
      <div className="mt-0.5 w-9 h-9 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div>
        <div className="font-semibold text-slate-900">{title}</div>
        <div className="text-sm text-slate-600">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, type = "text", placeholder, value, onChange, required, testid }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-semibold text-slate-700">{label}</span>
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 transition"
      />
    </label>
  );
}
